import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PartsTracker = ({ requestId }) => {
  const [expanded, setExpanded] = useState(false);
  const [parts, setParts] = useState([]);
  const [newPart, setNewPart] = useState({
    name: '',
    partNumber: '',
    quantity: 1,
    status: 'needed'
  });

  const handleAddPart = () => {
    if (!newPart?.name?.trim()) {
      alert('Please enter part name');
      return;
    }

    setParts(prev => [...prev, { ...newPart, id: Date.now() }]);
    setNewPart({ name: '', partNumber: '', quantity: 1, status: 'needed' });
  };

  const handleRemovePart = (id) => {
    setParts(prev => prev?.filter(p => p?.id !== id));
  };

  const handleStatusChange = (id, status) => {
    setParts(prev => prev?.map(p => p?.id === id ? { ...p, status } : p));
  };

  const getStatusConfig = (status) => {
    const configs = {
      'needed': { color: 'bg-warning/10 text-warning border-warning/20', icon: 'Clock', label: 'Needed' },
      'ordered': { color: 'bg-primary/10 text-primary border-primary/20', icon: 'ShoppingCart', label: 'Ordered' },
      'received': { color: 'bg-success/10 text-success border-success/20', icon: 'CheckCircle2', label: 'Received' },
      'installed': { color: 'bg-success/10 text-success border-success/20', icon: 'Check', label: 'Installed' }
    };
    return configs?.[status] || configs?.['needed'];
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-smooth"
      >
        <div className="flex items-center gap-2">
          <Icon name="Package" size={20} color="var(--color-primary)" strokeWidth={2} />
          <h2 className="text-lg font-semibold text-foreground">Parts Tracker</h2>
          {parts?.length > 0 && (
            <span className="bg-primary text-primary-foreground text-xs font-bold px-2 py-0.5 rounded-full">
              {parts?.length}
            </span>
          )}
        </div>
        <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={20} strokeWidth={2} />
      </button>

      {expanded && (
        <div className="p-4 space-y-4">
          {/* Add New Part */}
          <div className="bg-muted/30 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Add Part</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                label="Part Name"
                placeholder="e.g., Brake Pad Set"
                value={newPart?.name}
                onChange={(e) => setNewPart(prev => ({ ...prev, name: e?.target?.value }))}
              />
              <Input
                label="Part Number (Optional)"
                placeholder="e.g., BP-12345"
                value={newPart?.partNumber}
                onChange={(e) => setNewPart(prev => ({ ...prev, partNumber: e?.target?.value }))}
              />
            </div>
            <div className="flex gap-2">
              <Input
                type="number"
                label="Quantity"
                min="1"
                value={newPart?.quantity}
                onChange={(e) => setNewPart(prev => ({ ...prev, quantity: parseInt(e?.target?.value) || 1 }))}
                className="w-24"
              />
              <div className="flex-1 flex items-end">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={handleAddPart}
                  fullWidth
                >
                  Add Part
                </Button>
              </div>
            </div>
          </div>

          {/* Parts List */}
          {parts?.length > 0 ? (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">Required Parts</h3>
              {parts?.map(part => {
                const statusConfig = getStatusConfig(part?.status);
                return (
                  <div key={part?.id} className="bg-muted/30 rounded-lg p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-foreground">{part?.name}</h4>
                        {part?.partNumber && (
                          <p className="text-xs text-muted-foreground">Part #: {part?.partNumber}</p>
                        )}
                        <p className="text-xs text-muted-foreground">Quantity: {part?.quantity}</p>
                      </div>
                      <button
                        onClick={() => handleRemovePart(part?.id)}
                        className="text-error hover:text-error/80 transition-hover"
                      >
                        <Icon name="Trash2" size={16} strokeWidth={2} />
                      </button>
                    </div>
                    <div className="flex gap-2 mt-3">
                      {['needed', 'ordered', 'received', 'installed']?.map(status => {
                        const config = getStatusConfig(status);
                        return (
                          <button
                            key={status}
                            onClick={() => handleStatusChange(part?.id, status)}
                            className={`flex-1 px-2 py-1 rounded-md text-xs font-medium border transition-smooth ${
                              part?.status === status ? config?.color : 'bg-background text-muted-foreground border-border hover:bg-muted/30'
                            }`}
                          >
                            {config?.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <Icon name="Package" size={48} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No parts added yet</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PartsTracker;